import type { ButtonProps as RACButtonProps } from 'react-aria-components'
import { composeRenderProps, Button as RACButton } from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { Spinner } from './Spinner'
import { focusRing } from './utils'

export interface ButtonProps extends RACButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'lg' | 'md' | 'sm' | 'xs'
  isIconOnly?: boolean
}

const button = tv({
  extend: focusRing,
  base: `
    inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md
    text-center font-medium transition
    disabled:cursor-default disabled:opacity-50
    pending:opacity-50
  `,
  variants: {
    variant: {
      primary: `
        bg-dark-primary text-foreground-inverse
        hover:bg-dark-secondary
      `,
      secondary: `
        border border-light bg-transparent text-foreground
        hover:bg-hover-light
      `,
      ghost: `
        bg-transparent text-foreground
        hover:bg-hover-light
      `,
    },
    size: {
      lg: 'h-10',
      md: 'h-8',
      sm: 'h-7',
      xs: 'h-5',
    },
    isIconOnly: {
      true: 'aspect-square',
    },
  },
  compoundVariants: [
    {
      size: ['lg', 'md'],
      class: 'gap-1.5 text-sm',
    },
    {
      size: ['sm', 'xs'],
      class: 'gap-1 text-xs',
    },
    {
      size: 'lg',
      isIconOnly: false,
      class: 'px-4 py-1.5',
    },
    {
      size: ['md', 'sm'],
      isIconOnly: false,
      class: 'px-3 py-1.5',
    },
    {
      size: 'xs',
      isIconOnly: false,
      class: 'px-2',
    },
    {
      size: 'lg',
      isIconOnly: true,
      class: 'w-10 p-2',
    },
    {
      size: 'md',
      isIconOnly: true,
      class: 'w-8 p-2',
    },
    {
      size: 'sm',
      isIconOnly: true,
      class: 'w-7 p-1.5',
    },
    {
      size: 'xs',
      isIconOnly: true,
      class: 'w-5 p-1.5',
    },
  ],
  defaultVariants: {
    variant: 'primary',
    size: 'md',
    isIconOnly: false,
  },
})

export function Button(props: ButtonProps) {
  return (
    <RACButton
      {...props}
      className={composeRenderProps(
        props.className,
        (className, renderProps) => button({
          ...renderProps,
          variant: props.variant,
          size: props.size,
          isIconOnly: props.isIconOnly,
          className,
        }),
      )}
    >
      {({ isPending }) => (
        <>
          {isPending && <Spinner />}
          {!(isPending && props.isIconOnly) && props.children}
        </>
      )}
    </RACButton>
  )
}
